// StepCleanupSelect.tsx - v12.9.1 (#708): a compact header-row mode captioned "Cleanup", so it sits beside Step name and Confidence instead of below the question.
// v12.8.1 (#672) · StepCleanupSelect · VoiceSpectrum AI
//
// THE STEP LEVEL OF THE INPUT-CLEANUP CHAIN. IT WAS DECIDED, THE ENGINE READS IT, AND NO SCREEN HAS EVER SET IT.
//
// Tushar, 2026-09-23 (#641): *"input cleanup can stay on workflow step, didn't we keep it on fields also. so
// whatever is defined at field level will be shown in workflow and if required to change on workflow, it can be
// changed to a different kind cleanup."* Three levels: STEP -> FIELD -> ORGANISATION.
//
// Two of the three shipped. The organisation default is on Call Handling (12.7.3); the field override is the
// Vocabulary drawer (#659, 12.8.0). The STEP override has existed in the data model the whole time -
// `WorkflowStep.cleanup` is in the type, it is parsed on load, it round-trips on save, and
// `EffectiveConfigResolver:302` reads it as the TOP of the chain - with nothing anywhere to set it.
//
// He found it himself, 2026-09-24: *"aren't we supposed to have field clean up in workflow cards as well? why
// don't i see option her."*
//
// THIRD RELEASE RUNNING that the engine has honoured a setting no screen could reach: #567 (the action timeout
// shipped with no field), #659 (a control that saved nowhere), now this. The pattern is not carelessness at any
// one site - it is that "the engine reads it" and "a person can set it" are two facts that look like one.
//
// This component is deliberately separate rather than inlined, because the step card is rendered at TWO sites in
// WorkflowModalSnapshotPage and the file's own note warns that a grep for a control answers "yes" on the first
// one. A shared component makes the second site's absence a missing import rather than a silent gap.

export interface StepCleanupSelectProps {
  /** v12.9.1 (#708): the caption. "Cleanup" in the step header row, "Input cleanup" anywhere else. */
  label?: string;
  /** v12.9.1 (#708): header-row mode - no wrapper margins, so it lines up with Step name and Confidence. */
  compact?: boolean;
  /** The step's own override. null/undefined/'' all mean "inherit from the field". */
  value?: string | null;
  /** The field type, when known - AI assist cannot be offered on free text. */
  fieldType?: string;
  disabled?: boolean;
  onChange: (next: string | null) => void;
}

export function StepCleanupSelect({ value, fieldType, disabled, onChange, label, compact }: StepCleanupSelectProps) {
  const isText = (fieldType ?? '').toLowerCase() === 'text';
  return (
    <div className={compact ? 'rq-stepcleanup compact' : 'aa-f rq-stepcleanup'}>
      {compact
        ? <span className="lab plain">{label ?? 'Cleanup'}</span>
        : <label>{label ?? 'Input cleanup'}</label>}
      <select
        className="rq-in"
        value={value ?? ''}
        disabled={disabled}
        onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
      >
        {/* The empty option is the DEFAULT and must stay first: every existing step has no override, and
            opening a workflow must not silently change what any of them do. */}
        {/* v12.9.4 (#732): "Field default", not "Use the field's setting" - which truncated to
            "Use the field's set..." in his own screenshot, the worst of both: longer than the space AND
            unreadable. Same meaning, and it fits. */}
        <option value="">Field default</option>
        <option value="off">Raw &mdash; no processing</option>
        <option value="fast">Fast &mdash; deterministic</option>
        <option value="ai" disabled={isText}>
          AI assist{isText ? ' — not available on text' : ''}
        </option>
      </select>
      {/* v12.9.0 (#697): the explanatory line is GONE. Tushar, 2026-09-24: *"remove this extra text in
          tooltip for input cleanup."* The options say what they do, and the one case a reader cannot work out
          for themselves - why AI assist is greyed out on a text field - is written on the option itself. */}
    </div>
  );
}
